import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button } from '@/components/ui'
import { SelectField } from '@/components/ui/select'
import { CardImobiliariaCorretorBrasil } from '@/features/proposta/brasil/components/card-imobiliaria-corretor-brasil'
import type { CadastroImobiliariaCorretorEntity } from '@/features/proposta/brasil/domain/cadastro-imobiliaria-corretor.entity'
import type { TDetalhesPropostaSchema } from '@/features/proposta/brasil/domain/schemas/detalhes-proposta-schema'

type TCadastroImobiliariaCorretorFormProps = {
  cadastroImobiliariaCorretoresEntity: CadastroImobiliariaCorretorEntity
  exibeFormaPagamentoComissao?: boolean
}

export const CadastroImobiliariaCorretorForm = ({
  cadastroImobiliariaCorretoresEntity,
  exibeFormaPagamentoComissao,
}: TCadastroImobiliariaCorretorFormProps) => {
  const { t } = useTranslation('proposta')
  const { control, watch, trigger } = useFormContext<TDetalhesPropostaSchema>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'detalhesDaProposta.imobiliariasCorretores.participacoes',
  })

  const formaPagamentoComissao =
    watch('detalhesDaProposta.imobiliariasCorretores.formaPagamentoComissao') ?? 'PagaPelaPlaenge'

  const isAdicionarNovaParticipacaoDesabilitado =
    watch('detalhesDaProposta.imobiliariasCorretores.participacoes')?.some(
      (participacao) => !participacao.imobiliariaCorretor.idExterno,
    ) ?? false

  const isExibirCamposPreenchimento =
    !exibeFormaPagamentoComissao || (exibeFormaPagamentoComissao && formaPagamentoComissao)

  const handleSelecionarImobiliaria = (
    participacaoPath: `detalhesDaProposta.imobiliariasCorretores.participacoes.${number}`,
  ) => {
    trigger(participacaoPath)
  }

  return (
    <div className="border-muted flex w-full flex-col items-start border-1 p-4">
      <Render.If condition={exibeFormaPagamentoComissao}>
        {() => (
          <SelectField
            control={control}
            className="w-full lg:max-w-65"
            name="detalhesDaProposta.imobiliariasCorretores.formaPagamentoComissao"
            label={t('detalhes_proposta.imobiliaria.inputs.forma_comissao.label')}
            options={[
              { value: 'PagaPelaPlaenge', label: t('detalhes_proposta.imobiliaria.inputs.forma_comissao.plaenge') },
              { value: 'PagaPeloCliente', label: t('detalhes_proposta.imobiliaria.inputs.forma_comissao.cliente') },
            ]}
            labelKey="label"
            valueKey="value"
          />
        )}
      </Render.If>
      <Render.If condition={isExibirCamposPreenchimento}>
        {() => (
          <div className="mt-4 flex flex-col items-start gap-4 md:gap-1">
            {fields.map((participacao, index) => (
              <CardImobiliariaCorretorBrasil
                key={participacao.id}
                index={index}
                onSelecionarImobiliaria={handleSelecionarImobiliaria}
                onExcluirParticipacao={() => {
                  remove(index)
                  trigger('detalhesDaProposta.imobiliariasCorretores.participacoes')
                }}
              />
            ))}
            <Button
              variant="link"
              className="text-wrap whitespace-normal aria-disabled:cursor-not-allowed aria-disabled:border-neutral-500 aria-disabled:text-neutral-500"
              onClick={() => append(cadastroImobiliariaCorretoresEntity.novoCadastro)}
              aria-disabled={isAdicionarNovaParticipacaoDesabilitado}
              disabled={isAdicionarNovaParticipacaoDesabilitado}
            >
              <Icon name="plus" />
              {t('detalhes_proposta.imobiliaria.botoes.adicionar')}
            </Button>
          </div>
        )}
      </Render.If>
    </div>
  )
}
